<template>
  <label class="select">
    <span
      class="select__label"
      v-bind:class="{ 'select__label--help': needsHint }"
    >{{ title }}</span>
    <span
      class="select__hint"
      v-html="hint"
      v-if="needsHint" />
    <div class="select__wrapper">
      <select
        class="select__select"
        v-bind:class="{ empty: wasSelectedPlaceholder }"
        v-bind:disabled="disabled"
        v-bind:name="name"
        v-bind:required="!optional"
        v-bind:value="value"
        v-on:input="onInput"
      >
        <option
          disabled="disabled"
          selected="selected"
          v-bind:value="$options.placeholderOptionValue"
          v-if="hasPlaceholder"
        >{{ placeholder }}</option>
        <option
          v-for="option in mappedOptions"
          v-bind:disabled="option.disabled"
          v-bind:key="option.key"
          v-bind:value="option.value"
        >{{ option.title }}</option>
      </select>
      <button
        v-if="!wasSelectedPlaceholder && !disabled && clearable"
        type="button"
        class="select__clear"
        title="Очистить поле"
        aria-label="Очистить поле"
        v-on:click="clearSelection"
      />
    </div>
    <p
      class="text__violation"
      role="alert"
      v-for="violationText in violations"
      v-bind:key="violationText"
    >{{ violationText }}</p>
  </label>
</template>
<script>

/**
 * @description Название события ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

export default {
  computed: {
    /**
     * @description Имеет заполнитель.
     * @type {boolean}
     */
    hasPlaceholder() {
      return this.placeholder !== this.$options.placeholderOptionValue;
    },
    keyMapper() {
      const prefix = this.optionValueKey;
      return (value) => `${prefix}:${value}`;
    },
    mappedOptions() {
      return this.options.map((item) => {
        const disabled = Boolean(item.disabled);
        const value = this.valueMapper(item);
        const key = this.keyMapper(value);
        const title = this.titleMapper(item);
        return {
          disabled,
          key,
          title,
          value,
        };
      });
    },
    /**
     * @description Нужна подсказка.
     */
    needsHint() {
      return this.hint !== '' && this.disabled !== true;
    },
    titleMapper() {
      const key = this.optionTitleKey;
      return (option) => {
        if (typeof option === 'object') {
          return option[key] ?? '';
        }
        return option;
      };
    },
    valueMapper() {
      const key = this.optionValueKey;
      return (option) => {
        if (typeof option === 'object') {
          return option[key] ?? '';
        }
        return option;
      };
    },
    /**
     * @description Выбрана опция заполнителя.
     * @returns {boolean}
     */
    wasSelectedPlaceholder() {
      return this.hasPlaceholder
        && this.value === this.$options.placeholderOptionValue;
    },
  },
  methods: {
    /**
     * @description Обработать ввод.
     * @param {Event} event
     * @returns {void}
     */
    onInput(event) {
      this.$emit(NAME_EVENT_INPUT, event.target.value);
    },
    /**
     * @description Очистить выбор.
     * @returns {void}
     */
    clearSelection() {
      this.$emit(NAME_EVENT_INPUT, this.$options.placeholderOptionValue);
    },
  },
  name: 'TheSelect',
  placeholderOptionValue: '',
  props: {
    /**
     * @description Деактивирован.
     * @type {boolean}
     */
    disabled: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Уникальное имя элемента формы.
     * @type {String}
     */
    name: {
      default: () => false,
      type: [String, Boolean],
    },
    /**
     * @description Ключ заголовка опции.
     * @type {string}
     */
    optionTitleKey: {
      default: () => 'title',
      type: String,
    },
    /**
     * @description Ключ значения опции.
     * @type {string}
     */
    optionValueKey: {
      default: () => 'value',
      type: String,
    },
    /**
     * @description Допускается необязательный ввод.
     * @type {boolean}
     */
    optional: {
      default: () => true,
      type: Boolean,
    },
    /**
     * @description Опции.
     * @type {any[]}
     */
    options: {
      required: true,
      type: Array,
    },
    /**
     * @description Заполнитель
     * @type {string}
     */
    placeholder: {
      default() {
        return this.$options.placeholderOptionValue;
      },
      type: String,
    },
    /**
     * С кнопкой
     *
     */
    isWithCross: {
      default: true,
      type: Boolean,
      required: false,
    },
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: {
      default: '',
      type: String,
    },
    /**
     * @description Значение.
     * @type {(number|string)}
     */
    value: {
      default: '',
      type: [Number, String],
    },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => [],
      type: Array,
    },
    clearable: {
      default: () => false,
      type: Boolean,
    },
    hint: { required: false, type: String, default: '' },
  },
};
</script>
<style scoped lang="scss">
.select {
  position: relative;
  display: block;
  &__wrapper {
    position: relative;
  }
}
.select .select__label {
  color: var(--unfocus-color);
  display: block;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  margin: 0 0 3px 0;
  &--help {
    cursor: help;
  }
  &:hover + .select__hint {
    display: block;
  }
}
.select__hint {
  display: none;
  position: absolute;
  top: -75px;
  left: 0;
  background-color: white;
  color: rgba(0,0,0,.87);
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 0 20px rgba(54, 77, 92, 0.18);
  width: auto;
  font-size: 12px;
  z-index: 2;
  border: 1px solid #c2cfe0;
  pointer-events: none;
}
.select .select__select {
  background: var(--primary-background-color)
              url("@/assets/img/checked/checked-grey.svg")
              right
              1rem
              center
              no-repeat;
  border-radius: var(--input-border-radius);
  border: 1px solid var(--border-color);
  height: 2.5rem;
  padding: 0 2.5rem 0 .625rem;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.select .select__select:not(:disabled) {
  cursor: pointer;
}
.select:has(.select__clear) {
  .select__select {
    padding-right: 4rem;
  }
}
@supports not selector(:has) {
  .select__select {
    padding-right: 4rem;
  }
}
.select .select__select.empty {
  color: var(--unfocus-color);
}
.select .select__select:focus {
  background-color: var(--primary-background-color);
  border: 2px solid var(--action-color);
  outline: none;
}
.select .select__select option:disabled {
  color: var(--unfocus-color);
}
.select .select__clear {
  position: absolute;
  top: 50%;
  right: 2.5rem;
  width: 18px;
  height: 18px;
  mask: url("@/assets/img/cross.svg") no-repeat center;
  mask-size: 18px;
  background-color: #707683;
  transform: translateY(-50%);
  transition: opacity 0.3s;
  &:hover {
    opacity: 0.7;
  }
}
.text {
  &__label {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 2px 0;
    color: var(--unfocus-color);
  }
  &__input {
    background: var(--primary-background-color);
    border: 1px solid #C2CFE0;
    outline: none;
    height: 40px;
    width: 100%;
    padding: 0 10px;
    appearance: none;
    font-size: 16px;
    color: #192A3E;
    font-weight: 400;
    &:focus {
      background-color: var(--primary-background-color);
      border: 2px solid var(--action-color);
      color: var(--reading-color);
      outline: 0;
    }
    &:disabled {
      background-color: var(--primary-background-color);
    }
  }
  &.invalid .text__input{
    color: var(--warning-color);
  }
  &__violation {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
  }
}
</style>
